import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { KeyRound, Clock, CheckCircle2, XCircle, Ban, ExternalLink } from "lucide-react";
import api from "@/lib/api";

export default function CertificatesTab() {
  const [claims, setClaims] = useState(null);
  useEffect(() => {
    api.get("/me/claims")
      .then((r) => setClaims(r.data.claims || []))
      .catch(() => setClaims([]));
  }, []);

  if (claims === null) {
    return (
      <div className="space-y-3" data-testid="certs-loading">
        {[1, 2, 3].map((i) => (
          <div key={i} className="rounded-2xl bg-white border-2 border-[var(--line)] p-5 gm-skel h-24" />
        ))}
      </div>
    );
  }

  const owned = claims.filter((c) => c.status === "approved");
  const pending = claims.filter((c) => c.status === "pending");
  const other = claims.filter((c) => !["approved", "pending"].includes(c.status));

  return (
    <div className="space-y-10" data-testid="certificates-tab">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h3 className="font-display text-2xl font-bold text-[var(--ink)]">Your certificates</h3>
          <p className="mt-1 text-sm text-[var(--smoke)]">
            Everything you own — plus anything under review.
          </p>
        </div>
        <Link to="/claim" className="btn-red text-sm shrink-0" data-testid="certs-claim-cta">
          <KeyRound size={16} /> Claim another certificate
        </Link>
      </div>

      <Bucket title="Owned" count={owned.length} claims={owned} emptyCopy="No claimed certificates yet." />
      <Bucket title="Under review" count={pending.length} claims={pending} emptyCopy="No claims are waiting on our team." />
      {other.length > 0 && (
        <Bucket title="Archive" count={other.length} claims={other} emptyCopy="" />
      )}
    </div>
  );
}

function Bucket({ title, count, claims, emptyCopy }) {
  return (
    <section data-testid={`bucket-${title.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-center gap-3 mb-3">
        <h4 className="text-[11px] font-bold tracking-widest text-[var(--ink)] uppercase">{title}</h4>
        <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-[var(--panini-yellow-soft)] text-[var(--ink)]">
          {count}
        </span>
      </div>
      {claims.length === 0 ? (
        <div className="rounded-2xl border-2 border-dashed border-[var(--line)] bg-white p-6 text-sm text-[var(--smoke)]">
          {emptyCopy}
        </div>
      ) : (
        <div className="space-y-3">
          {claims.map((c) => <ClaimRow key={c.id} claim={c} />)}
        </div>
      )}
    </section>
  );
}

function ClaimRow({ claim }) {
  return (
    <div className="rounded-2xl border-2 border-[var(--ink)] bg-white p-4 md:p-5 flex flex-col md:flex-row md:items-center gap-3 shadow-[3px_3px_0_var(--ink)]"
          data-testid={`cert-row-${claim.id}`}>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-3 flex-wrap">
          <StatusBadge status={claim.status} />
          <span className="font-mono text-[var(--panini-red)] font-bold">{claim.cert_number}</span>
          <span className="text-xs text-[var(--smoke)] uppercase tracking-widest">
            {claim.method === "claim_code" ? "Claim code" : "Legacy proof"}
          </span>
        </div>
        {claim.review_notes && (
          <div className="mt-2 text-sm text-[var(--ink)] bg-[var(--panini-yellow-soft)] rounded-lg px-3 py-2">
            <span className="font-bold">Reviewer note:</span> {claim.review_notes}
          </div>
        )}
        <div className="text-[11px] text-[var(--smoke)] mt-2">
          {new Date(claim.created_at).toLocaleString()}
          {claim.reviewed_at ? ` · Reviewed ${new Date(claim.reviewed_at).toLocaleString()}` : ""}
        </div>
      </div>
      <Link
        to={`/certificates/${claim.cert_number}`}
        data-testid={`cert-link-${claim.id}`}
        className="text-xs font-bold text-[var(--ink)] inline-flex items-center gap-1 hover:text-[var(--panini-red)]"
      >
        View public page <ExternalLink size={13} />
      </Link>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    approved: { bg: "#dcfce7", fg: "#166534", Icon: CheckCircle2, label: "Owned" },
    pending:  { bg: "#fef3c7", fg: "#92400e", Icon: Clock,        label: "Under review" },
    rejected: { bg: "#fee2e2", fg: "#991b1b", Icon: XCircle,      label: "Not approved" },
    cancelled:{ bg: "#e5e7eb", fg: "#374151", Icon: Ban,          label: "Cancelled" },
    revoked:  { bg: "#e5e7eb", fg: "#374151", Icon: Ban,          label: "Revoked" },
  };
  const cfg = map[status] || map.pending;
  const Icon = cfg.Icon;
  return (
    <span
      className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-widest"
      style={{ background: cfg.bg, color: cfg.fg }}
    >
      <Icon size={12} /> {cfg.label}
    </span>
  );
}
